'use client';
/**
 * Agrandir la colonne de droite d'un écran maître-détail.
 *
 * Demande de la RQ le 14/08/2026 : « quand l'affichage est en deux colonnes, je
 * voudrais pouvoir ouvrir en grand écran la colonne de droite — à étendre à
 * toutes les situations deux colonnes, pas uniquement les équipements ».
 *
 * ── Pourquoi un contrôle UNIQUE et non un bouton par écran ──────────────────
 *
 * Six écrans portent aujourd'hui une grille maître-détail (équipements,
 * réclamations, actions, audits, revues de direction, calibrations), et il s'en
 * ajoute à chaque lot. Poser un bouton dans chacun, c'est six insertions à
 * refaire à chaque nouvel écran — et l'oubli ne se voit pas : l'écran marche,
 * il lui manque seulement une commande que les cinq autres ont.
 *
 * Ce composant est donc monté UNE fois par le Shell. Il cherche la grille
 * (`.ged-colonnes-defilantes`) dans la page ; s'il n'y en a pas, il ne s'affiche
 * pas. Un écran maître-détail créé demain héritera de la commande sans qu'on y
 * touche.
 *
 * ── Pourquoi passer par un attribut plutôt que par un état React ────────────
 *
 * La grille appartient à l'écran, pas à ce composant : la piloter par un état
 * partagé supposerait que les six écrans le lisent, c'est-à-dire exactement les
 * six retouches qu'on évite. On pose donc `data-agrandi` sur la grille et la
 * feuille de style fait le reste — la colonne de gauche se replie, la droite
 * prend toute la largeur. Aucun composant n'est démonté : l'éditeur en cours,
 * le défilement et la saisie survivent au basculement.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { I } from '@/components/ui';

const SELECTEUR = '.ged-colonnes-defilantes';
/** Sous ce seuil il n'y a plus deux colonnes (cf. `globals.css`) : la commande
 *  n'aurait rien à agrandir et prêterait à confusion. */
const LARGEUR_MINI = 981;

export function AgrandirColonne() {
  const [grille, setGrille] = useState<HTMLElement | null>(null);
  const [agrandi, setAgrandi] = useState(false);
  const [assezLarge, setAssezLarge] = useState(true);

  // La grille apparaît et disparaît au fil des écrans : on la cherche après
  // chaque rendu du Shell plutôt qu'une seule fois au montage.
  useEffect(() => {
    const chercher = () => {
      const el = document.querySelector(SELECTEUR) as HTMLElement | null;
      const large = window.innerWidth >= LARGEUR_MINI;
      /* ⚠️ La fiche élargie se REPLIE quand la fenêtre devient trop étroite.
       *
       * Sans cela : on élargit la fiche, puis on rétrécit la fenêtre (ou on
       * zoome, ce qui revient au même en pixels CSS). Le bouton disparaît —
       * il n'a plus rien à agrandir sous ce seuil — mais l'attribut
       * `data-agrandi` reste posé sur la grille, et la règle de style qui
       * MASQUE la colonne de gauche, elle, n'a pas de seuil.
       * Résultat : la liste reste cachée, et la commande qui la ramènerait
       * n'est plus à l'écran. Échap fonctionne encore, mais rien ne le dit.
       * C'est une porte qui se ferme à clé de l'intérieur. */
      if (el && !large && el.getAttribute('data-agrandi') === '1') {
        el.removeAttribute('data-agrandi');
      }
      setGrille(el);
      setAgrandi(el?.getAttribute('data-agrandi') === '1');
      setAssezLarge(large);
    };
    chercher();
    const obs = new MutationObserver(chercher);
    obs.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', chercher);
    return () => { obs.disconnect(); window.removeEventListener('resize', chercher); };
  }, []);

  const basculer = useCallback(() => {
    const el = document.querySelector(SELECTEUR) as HTMLElement | null;
    if (!el) return;
    const suivant = el.getAttribute('data-agrandi') !== '1';
    if (suivant) el.setAttribute('data-agrandi', '1');
    else el.removeAttribute('data-agrandi');
    setAgrandi(suivant);
  }, []);

  // Échap réduit — même geste que partout ailleurs dans l'application.
  useEffect(() => {
    if (!agrandi) return;
    const surTouche = (e: KeyboardEvent) => { if (e.key === 'Escape') basculer(); };
    window.addEventListener('keydown', surTouche);
    return () => window.removeEventListener('keydown', surTouche);
  }, [agrandi, basculer]);

  if (!grille || !assezLarge) return null;

  return (
    <button type="button" onClick={basculer} aria-pressed={agrandi}
      title={agrandi
        ? 'Réafficher la liste à gauche (Échap)'
        : 'Masquer la liste et donner toute la largeur à la fiche'}
      style={{
        position: 'fixed',
        // Ancrée au contenu et non à la fenêtre — cf. la note de `GommetteNC`.
        insetInlineEnd: 'max(18px, calc((100vw - var(--container-xl)) / 2 + 18px))',
        bottom: 84, zIndex: 60,
        display: 'inline-flex', alignItems: 'center', gap: 7,
        fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: 600,
        background: '#fff', color: 'var(--text-body)',
        border: '1px solid var(--border-default)', borderRadius: 'var(--radius-pill)',
        padding: '7px 13px', cursor: 'pointer',
        boxShadow: 'var(--shadow-sm, 0 2px 8px rgba(14,39,64,0.12))',
      }}>
      <I n={agrandi ? 'x' : 'expand'} s={13} />
      {agrandi ? 'Réafficher la liste' : 'Élargir la fiche'}
    </button>
  );
}
